import axios from 'axios';
import { getApiUrl } from '../utils/apiClient';



const API_BASE_URL = getApiUrl();

// デバッグ用（本番環境では削除）
console.log('[AuthAPI v2] Current hostname:', window.location.hostname);
console.log('[AuthAPI v2] API Base URL:', API_BASE_URL);
console.log('[AuthAPI v2] Build time:', new Date().toISOString());

// APIクライアント作成
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// リクエストインターセプター：トークンを自動的に付与
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('waton_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター：認証エラー時の処理
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 認証エラーの場合はトークンを削除
      localStorage.removeItem('waton_token');
      // ログインページにリダイレクト（必要に応じて）
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  user?: {
    id: number;
    username: string;
    displayName: string;
    roleLevel: number;
  };
}

export interface VerifyResponse {
  success: boolean;
  message?: string;
  user?: {
    id: number;
    username: string;
    displayName: string;
    roleLevel: number;
  };
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

// 認証API
export const authAPI = {
  // ログイン
  login: async (username: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'ログインに失敗しました',
      };
    }
  },

  // ログアウト
  logout: async (): Promise<LogoutResponse> => {
    try {
      const response = await apiClient.post('/auth/logout');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'ログアウトに失敗しました',
      };
    }
  },

  // トークン検証
  verifyToken: async (): Promise<VerifyResponse> => {
    try {
      const response = await apiClient.post('/auth/verify');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'トークンの検証に失敗しました',
      };
    }
  },

  // パスワード変更
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<ChangePasswordResponse> => {
    try {
      const response = await apiClient.put('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'パスワードの変更に失敗しました',
      };
    }
  },

  // 管理者パスワード検証（一時管理者権限用）
  verifyAdminPassword: async (password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await apiClient.post('/auth/verify-admin-password', {
        password,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'パスワードの検証に失敗しました',
      };
    }
  },
};

// スタッフ管理API
export interface Staff {
  id: number;
  username: string;
  displayName: string;
  roleLevel: number;
  roleLabel: string;
  isActive: boolean;
  email?: string;
  phone?: string;
  hireDate?: string;
  lastLogin?: string;
  createdAt: string;
}

export interface StaffListResponse {
  success: boolean;
  data?: Staff[];
  message?: string;
}

export interface StaffDetailResponse {
  success: boolean;
  data?: Staff;
  message?: string;
}

export interface StaffCreateResponse {
  success: boolean;
  data?: {
    id: number;
    username: string;
    displayName: string;
    roleLevel: number;
    roleLabel: string;
  };
  message?: string;
}

export interface CreateStaffData {
  username: string;
  password: string;
  displayName: string;
  roleLevel: number;
  email?: string;
  phone?: string;
  hireDate?: string;
}

export interface UpdateStaffData {
  username: string;
  displayName: string;
  roleLevel?: number;
  email?: string;
  phone?: string;
  hireDate?: string;
  isActive?: boolean;
}

export const staffAPI = {
  // スタッフ一覧取得
  getStaffList: async (): Promise<StaffListResponse> => {
    try {
      const response = await apiClient.get('/staff');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'スタッフ一覧の取得に失敗しました',
      };
    }
  },

  // スタッフ詳細取得
  getStaffDetail: async (id: number): Promise<StaffDetailResponse> => {
    try {
      const response = await apiClient.get(`/staff/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'スタッフ情報の取得に失敗しました',
      };
    }
  },

  // スタッフ作成
  createStaff: async (data: CreateStaffData): Promise<StaffCreateResponse> => {
    try {
      const response = await apiClient.post('/staff', data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'スタッフの作成に失敗しました',
      };
    }
  },

  // スタッフ更新
  updateStaff: async (id: number, data: UpdateStaffData): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.put(`/staff/${id}`, data);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'スタッフ情報の更新に失敗しました',
      };
    }
  },

  // スタッフパスワードリセット
  resetStaffPassword: async (id: number, newPassword: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.put(`/staff/${id}/reset-password`, {
        newPassword,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'パスワードのリセットに失敗しました',
      };
    }
  },

  // スタッフ無効化
  deactivateStaff: async (id: number): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiClient.delete(`/staff/${id}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'スタッフの無効化に失敗しました',
      };
    }
  },

  // 自分のプロフィール取得
  getMyProfile: async (): Promise<StaffDetailResponse> => {
    try {
      const response = await apiClient.get('/staff/me/profile');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'プロフィールの取得に失敗しました',
      };
    }
  },
};

// 操作ログAPI
export interface OperationLog {
  id: number;
  staffId?: number;
  username: string;
  action: string;
  targetTable?: string;
  targetId?: string;
  oldValues?: any;
  newValues?: any;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  success: boolean;
  errorMessage?: string;
  timestamp: string;
  staffDisplayName?: string;
}

export interface LogsListResponse {
  success: boolean;
  data?: {
    logs: OperationLog[];
    pagination: {
      current: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message?: string;
}

export const logsAPI = {
  // 操作ログ一覧取得
  getOperationLogs: async (params?: {
    page?: number;
    limit?: number;
    staffId?: number;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<LogsListResponse> => {
    try {
      const response = await apiClient.get('/logs/operations', { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || '操作ログの取得に失敗しました',
      };
    }
  },

  // ログイン試行ログ一覧取得
  getLoginAttempts: async (params?: {
    page?: number;
    limit?: number;
    username?: string;
    success?: boolean;
    startDate?: string;
    endDate?: string;
  }): Promise<LogsListResponse> => {
    try {
      const response = await apiClient.get('/logs/login-attempts', { params });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'ログイン試行ログの取得に失敗しました',
      };
    }
  },
};

export default apiClient;